import { Button, Flex, Text } from '@mantine/core';
import { WithFallback } from '@shared/ui/WithFallback';
import { IconArrowUpRight } from '@tabler/icons-react';
import { type ReactNode, Fragment } from 'react';
import { NavLink } from 'react-router';
import classes from './ReviewsPreview.module.scss';

type ReviewsPreviewData<T> = {
  data: T[];
  pagination: {
    total: number;
  };
};

type ReviewsPreviewProps<T extends { id: number }> = {
  data: ReviewsPreviewData<T> | null | undefined;
  isLoading: boolean;
  viewAllHref: string;
  previewCount?: number;
  title?: string;
  emptyText?: string;
  renderCard: (review: T) => ReactNode;
};

const DEFAULT_PREVIEW_COUNT = 3;

export const ReviewsPreview = <T extends { id: number }>({
  data,
  isLoading,
  viewAllHref,
  previewCount = DEFAULT_PREVIEW_COUNT,
  title = 'Отзывы',
  emptyText = 'Нет ни одного отзыва',
  renderCard,
}: ReviewsPreviewProps<T>) => {
  return (
    <Flex
      className={classes.wrapper}
      gap={24}
      direction='column'
      maw={418}
      miw={{ base: 'auto', sm: '25.5rem' }}
    >
      <WithFallback data={data} isLoading={isLoading}>
        {reviewsData => {
          const reviews = reviewsData.data.slice(0, previewCount);
          const total = reviewsData.pagination.total;

          return (
            <>
              <Flex w='100%' justify='space-between' align='center' wrap='wrap'>
                <Text size='lg' fw={600}>
                  {title}
                </Text>

                <Button
                  variant='subtle'
                  rightSection={<IconArrowUpRight size={20} />}
                  className={classes.allButton}
                  component={NavLink}
                  to={viewAllHref}
                >
                  {total ? `Все (${total})` : 'Все'}
                </Button>
              </Flex>

              {reviews.length === 0 ? (
                <Text c='tertiary.7'>{emptyText}</Text>
              ) : (
                <Flex direction='column' gap={12}>
                  {reviews.map(review => (
                    <Fragment key={review.id}>{renderCard(review)}</Fragment>
                  ))}
                </Flex>
              )}
            </>
          );
        }}
      </WithFallback>
    </Flex>
  );
};
